import {ProblemDetails} from '../types';

// The SPA is served by the Worker, so relative URLs work everywhere, including `cf dev`.
export const baseUrl = '';

export class ApiError extends Error {
    status: number;
    problem: ProblemDetails | null;
    /** The field a validation error belongs to, when the server said. */
    key: string | null;
    /** From Retry-After on a 429, so we can say how long rather than just "too many". */
    retryAfterSeconds: number | null;

    constructor(status: number, message: string, problem: ProblemDetails | null = null, retryAfterSeconds: number | null = null) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.problem = problem;
        this.key = problem?.errors?.[0]?.key ?? null;
        this.retryAfterSeconds = retryAfterSeconds;
    }
}

/** "a few seconds" / "about a minute" / "about 3 minutes" — never a bare number of seconds. */
export const waitPhrase = (seconds: number | null): string => {
    if (seconds === null || !Number.isFinite(seconds) || seconds <= 20) return 'a few seconds';
    if (seconds <= 90) return 'about a minute';
    return `about ${Math.round(seconds / 60)} minutes`;
};

/** Retry-After is seconds here; an HTTP-date is also legal, so fall back to null. */
export const parseRetryAfter = (value: string | null): number | null => {
    if (!value) return null;
    const seconds = Number(value.trim());
    if (Number.isFinite(seconds) && seconds >= 0) return seconds;
    const at = Date.parse(value);
    return Number.isNaN(at) ? null : Math.max(0, Math.round((at - Date.now()) / 1000));
};

/**
 * Whether a failed call is worth trying again. A 4xx is the server saying no, and retrying a 429
 * is the one thing that makes it worse — which is how a loop turns a busy minute into an outage.
 */
export const isRetryable = (error: unknown): boolean => {
    const status = (error as {status?: number} | null)?.status;
    return status === undefined || status < 400 || status >= 500;
};

const defaultMessage = (status: number): string => {
    switch (status) {
        case 401:
            return 'You need to sign in again.';
        case 403:
            return "You don't have access to that.";
        case 404:
            return 'Not found.';
        case 429:
            return 'That was a lot at once. Try again in a moment.';
        default:
            return 'Something went wrong. Try again in a moment.';
    }
};

export async function handleResponse<T>(response: Response): Promise<T> {
    if (response.ok) {
        return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
    }

    const retryAfter = parseRetryAfter(response.headers.get('Retry-After'));
    // A 429 always says it in our own words: the server's wording is for scripts and assistants.
    if (response.status === 429) {
        await response.body?.cancel();
        throw new ApiError(429, `That was a lot at once. Try again in ${waitPhrase(retryAfter)}.`, null, retryAfter);
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('json')) {
        const problem = (await response.json().catch(() => null)) as ProblemDetails | null;
        const message =
            problem?.errors?.map(e => e.message).filter(Boolean).join(' ') ||
            problem?.detail ||
            (problem?.title && problem.title !== 'There were errors with your request.' ? problem.title : null) ||
            defaultMessage(response.status);
        throw new ApiError(response.status, message, problem, retryAfter);
    }
    throw new ApiError(response.status, defaultMessage(response.status), null, retryAfter);
}

export const jsonInit = (method: string, body?: unknown): RequestInit => ({
    method,
    headers: {'Content-Type': 'application/json'},
    body: body === undefined ? undefined : JSON.stringify(body)
});

/** Anonymous call (sign-in, token redeem, app info). */
export const api = async <T>(path: string, init?: RequestInit): Promise<T> =>
    handleResponse<T>(await fetch(`${baseUrl}${path}`, init));

export const errorMessage = (error: unknown): string =>
    error instanceof Error ? error.message : 'Something went wrong.';
